import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom, timeout } from 'rxjs';

import { environment } from 'src/environments/environment';
import {
  SyncPermanentError,
  SyncTransientError,
} from '../errors/offline.errors';
import { EntityName, SyncOperationType } from '../models/sync.model';

// ── Wire protocol ──────────────────────────────────────────────────────────
// Shared, verbatim, with back/src/modules/sync. Any change here needs the same
// change there; these two files are the contract.

/** One mutation as sent to POST /api/v1/sync. */
export interface SyncPushOperation {
  /** Idempotency key. The server records it and replays the stored result. */
  opId: string;
  entity: EntityName;
  operation: SyncOperationType;
  /** Record identity, independent of any server id. */
  uuid: string;
  /** Known server id, or null for a create the server has not seen. */
  serverId: number | null;
  /** Server version this change was made against. Drives conflict detection. */
  baseVersion: number;
  /** Record body. Null for deletes. */
  payload: Record<string, unknown> | null;
  /** Client clock, for audit and tie-breaking only - never trusted for ordering. */
  clientUpdatedAt: number;
}

export interface SyncPushRequest {
  operations: SyncPushOperation[];
  /** Client build, so the server can reject payloads it cannot interpret. */
  clientVersion: string;
}

/** Per-operation outcome. The client applies these one by one. */
export type SyncPushResultStatus = 'applied' | 'conflict' | 'rejected' | 'skipped';

export interface SyncPushResult {
  opId: string;
  status: SyncPushResultStatus;
  /** Present when applied: the authoritative id and version to store locally. */
  serverId?: number;
  version?: number;
  updatedAt?: number;
  /**
   * The identity the server actually holds, which is not always the one sent.
   * A record the server already had - saved online, or derived - keeps its own
   * uuid when a client create is applied to it. The client adopts this so both
   * sides agree on identity and a later pull matches instead of duplicating.
   */
  uuid?: string;
  /** Present when conflict: what the server actually holds right now. */
  serverRecord?: Record<string, unknown>;
  serverVersion?: number;
  /** Present when rejected: why, for display and logging. */
  message?: string;
}

export interface SyncPushResponse {
  results: SyncPushResult[];
  serverTime: number;
}

/** One changed record from GET /api/v1/sync/changes. */
export interface SyncChangeRecord {
  uuid: string | null;
  serverId: number;
  version: number;
  updatedAt: number;
  deleted: boolean;
  /** The row itself, in the server's column names. */
  data: Record<string, unknown>;
}

export interface SyncChangesResponse {
  entity: EntityName;
  records: SyncChangeRecord[];
  /** Feed back as `since` on the next call. */
  nextCursor: string | null;
  /** True when more pages are waiting; pull again immediately. */
  hasMore: boolean;
  serverTime: number;
}

/**
 * HTTP for the sync endpoints.
 *
 * Deliberately uses HttpClient directly rather than the app's ApiService: that
 * service pops alerts and can redirect on error, which is right for a user
 * action and wrong for a background loop. Sync failures are data for
 * SyncService to decide about, not something to interrupt the teacher with.
 *
 * Errors are classified here, because the distinction decides what happens
 * next: transient means retry with backoff, permanent means stop and surface.
 */
@Injectable({ providedIn: 'root' })
export class SyncApiService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browser = isPlatformBrowser(this.platformId);

  private readonly baseUrl = `${(environment.apiUrl ?? '').replace(/\/+$/, '')}/api/v1/sync`;

  /** A big batch on a weak connection times out; this keeps failures quick. */
  private static readonly REQUEST_TIMEOUT_MS = 30_000;
  private static readonly CLIENT_VERSION = '1';

  /**
   * The session token, or null when nobody is signed in.
   *
   * Read at call time rather than cached: APP_INITIALIZER runs long before the
   * login screen, so a value captured at construction would be null for the
   * whole session.
   */
  private authToken(): string | null {
    if (!this.browser) return null;
    return localStorage.getItem('token') ?? localStorage.getItem('accessToken');
  }

  /** Whether there is any point in talking to the sync endpoints yet. */
  hasAuthToken(): boolean {
    return this.authToken() !== null;
  }

  /** The session token, for a request sent past HttpClient's interceptors. */
  bearerToken(): string | null {
    return this.authToken();
  }

  /**
   * Who the current token belongs to, as an opaque key.
   *
   * Read from the token's own payload rather than from the various user objects
   * the app keeps in localStorage: the token is the thing the server actually
   * scopes data by, so it is the only identity guaranteed to match what a pull
   * returns.
   *
   * The signature is NOT verified here, and does not need to be - this decides
   * which local cache to use, not what anyone is allowed to see. The server
   * remains the only authority on that.
   */
  currentUserKey(): string | null {
    const token = this.authToken();
    if (!token) return null;

    try {
      const [, payload] = token.split('.');
      if (!payload) return null;
      const json = JSON.parse(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
      ) as Record<string, unknown>;

      const id =
        json['teacher_id'] ?? json['user_id'] ?? json['id'] ?? json['sub'];
      return id === undefined || id === null ? null : `u:${String(id)}`;
    } catch {
      // A token shape we do not recognise. Returning null means "do not switch
      // scope", which keeps the existing cache rather than wiping it on a guess.
      return null;
    }
  }

  private headers(): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const token = this.authToken();
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }

  /** Send a batch of local changes. */
  async push(operations: SyncPushOperation[]): Promise<SyncPushResponse> {
    const body: SyncPushRequest = {
      operations,
      clientVersion: SyncApiService.CLIENT_VERSION,
    };
    try {
      return await firstValueFrom(
        this.http
          .post<SyncPushResponse>(this.baseUrl, body, { headers: this.headers() })
          .pipe(timeout(SyncApiService.REQUEST_TIMEOUT_MS)),
      );
    } catch (error) {
      throw this.classify(error);
    }
  }

  /** Fetch server changes for one entity since the given cursor. */
  async changes(
    entity: EntityName,
    since: string | number | null,
    limit = 500,
  ): Promise<SyncChangesResponse> {
    const params = new URLSearchParams({
      entity,
      limit: String(limit),
    });
    if (since !== null) params.set('since', String(since));

    try {
      return await firstValueFrom(
        this.http
          .get<SyncChangesResponse>(`${this.baseUrl}/changes?${params.toString()}`, {
            headers: this.headers(),
          })
          .pipe(timeout(SyncApiService.REQUEST_TIMEOUT_MS)),
      );
    } catch (error) {
      throw this.classify(error);
    }
  }

  /**
   * Split failures into retry-worthy and not.
   *
   * status 0 covers both a dropped connection and a CORS failure; treating it
   * as transient is right for the first and harmless for the second, which will
   * simply keep failing until someone looks at the logs.
   */
  private classify(error: unknown): Error {
    if (error instanceof HttpErrorResponse) {
      const { status } = error;
      if (status === 0 || status === 408 || status === 429 || status >= 500) {
        return new SyncTransientError(
          `Sync request failed (${status || 'network'}), will retry.`,
          error,
        );
      }
      if (status === 401 || status === 403) {
        // The session is gone. Retrying cannot fix it and would spin until the
        // user signs in again, so stop and let the app handle re-auth.
        return new SyncPermanentError(
          'Not authorised to sync. Sign in again to continue.',
          error,
        );
      }
      return new SyncPermanentError(
        `Server rejected the sync request (${status}).`,
        error,
      );
    }

    // rxjs timeout, or anything else non-HTTP: worth another go later.
    return new SyncTransientError('Sync request did not complete.', error);
  }
}
